import { Tooltip } from "antd";
import { FC, useMemo } from "react";
import { MarketProductProgress, ProductPlan } from "tm-shared/types";
import { formatNumber } from "tm-shared/utils";

import BIcon from "@/assets/icons/kpi-b.svg";
import LIcon from "@/assets/icons/kpi-l.svg";
import MIcon from "@/assets/icons/kpi-m.svg";
import FlexContainer from "@/components/FlexContainer";

import { StyledProductInfoText } from "../../GeneralProductCard.styled";
import {
    StyledKpiInfoIcon,
    StyledMainKpiValue,
    StyledTooltipLabel,
    StyledTooltipValue,
} from "./ProductKpiInfo.styled";

const ICON_MAP = [
    { key: "b", icon: BIcon, alt: "Bottle icon" },
    { key: "l", icon: LIcon, alt: "Volume icon" },
    { key: "m", icon: MIcon, alt: "Currency icon" },
] as const;

type KpiValues = Partial<Record<"b" | "l" | "m", number>>;

type Props = {
    plan?: ProductPlan | null;
    fact?: MarketProductProgress | null;
    additionalPlan?: ProductPlan | null;
    onTouchStart?: () => void;
};

export const ProductKpiInfo: FC<Props> = ({
    plan,
    fact,
    additionalPlan,
    onTouchStart,
}) => {
    const factKpi: KpiValues | null = useMemo(
        () =>
            fact ? { b: fact.bottle, l: fact.capacity, m: fact.money } : null,
        [fact],
    );

    const hasValues = (values?: KpiValues | ProductPlan | null) =>
        !!values && Object.values(values).some((v) => v && v !== 0);

    const renderRow = (label: string | null, values: KpiValues) => (
        <FlexContainer
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            gap="10px"
        >
            {label && <StyledTooltipLabel>{label}</StyledTooltipLabel>}
            <FlexContainer gap="10px">
                {ICON_MAP.map(({ key, icon, alt }) => (
                    <FlexContainer key={key} alignItems="center" gap="4px">
                        <StyledTooltipValue>
                            {formatNumber(values[key] ?? 0, 2)}
                        </StyledTooltipValue>
                        <img src={icon} alt={alt} />
                    </FlexContainer>
                ))}
            </FlexContainer>
        </FlexContainer>
    );

    const renderBlock = (
        title: string,
        planValues?: ProductPlan | null,
        factValues?: KpiValues | null,
        tooltipDescription?: string,
    ) => {
        if (!hasValues(planValues) && !hasValues(factValues)) return null;

        const planValue = planValues?.b ?? 0;
        const factValue = factValues?.b ?? 0;
        const hasPlan = planValue > 0;
        const hasFact = factValue > 0;
        const diff =
            hasPlan && hasFact ? Math.abs(planValue - factValue) : null;

        const tooltipContent = (
            <FlexContainer flexDirection="column" gap="8px">
                {tooltipDescription && (
                    <StyledTooltipLabel>
                        {tooltipDescription}
                    </StyledTooltipLabel>
                )}
                {hasPlan && renderRow("План:", planValues!)}
                {hasFact && renderRow("Факт:", factValues!)}
            </FlexContainer>
        );

        return (
            <StyledProductInfoText
                onTouchStart={onTouchStart}
                style={{ touchAction: "manipulation" }}
            >
                {hasPlan && (
                    <>
                        {title}{" "}
                        <StyledMainKpiValue>{planValue} пл.</StyledMainKpiValue>
                    </>
                )}
                {hasFact && (
                    <>
                        {hasPlan && " / "}Факт:{" "}
                        <StyledMainKpiValue>{factValue} пл.</StyledMainKpiValue>
                    </>
                )}
                {hasPlan && hasFact && planValue !== factValue && (
                    <>
                        {" "}
                        ({planValue > factValue ? "<" : ">"}
                        {diff})
                    </>
                )}{" "}
                <Tooltip placement="top" title={tooltipContent}>
                    <StyledKpiInfoIcon />
                </Tooltip>
            </StyledProductInfoText>
        );
    };

    const blocks = [
        { title: "План:", plan, fact: factKpi },
        {
            title: "Додатковий план:",
            plan: additionalPlan,
            fact: factKpi,
            tooltip: "План з видалених ТТ маршруту",
        },
        { title: "Факт:", plan: null, fact: factKpi },
    ];

    for (const block of blocks) {
        if (hasValues(block.plan) || hasValues(block.fact)) {
            return (
                <>
                    {renderBlock(
                        block.title,
                        block.plan,
                        block.fact,
                        block.tooltip,
                    )}
                </>
            );
        }
    }

    return null;
};
